import { http } from "../../services/http";

export const API_RESOURCE = "forms";

/* ----------------------------------------------------------------- IDs ---- */
export function genId(prefix = "f") {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

/* Tipos de campo sem valor (não participam de lógica/cálculo/escore no builder). */
export const LAYOUT_FIELD_TYPES = ["section", "paragraph"];

/* Substitui o item em `idx` por `{ ...item, ...patch }`, mantendo os demais. */
export function updateAt(list, idx, patch) {
  return list.map((item, i) => (i === idx ? { ...item, ...patch } : item));
}

/* ------------------------------------------------------------- Datas ---- */
export function fmtDateTime(v) {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d) ? "-" : d.toLocaleString("pt-BR");
}

/* Cores de faixa de risco clínico, usadas nos chips de escore e no PDF exportado. */
export const RISK_COLOR = {
  "Muito Baixo": "#2e7d32", Baixo: "#66bb6a", Nenhum: "#9e9e9e",
  Intermediário: "#f9a825", Alto: "#ef6c00", "Muito Alto": "#c62828",
};

/* ----------------------------------------------- Registry de tipos de campo */
// category: "entrada" | "escolha" | "layout"
export const FIELD_TYPES = [
  { type: "text",      label: "Texto curto",     icon: "fa fa-font",          category: "entrada" },
  { type: "textarea",  label: "Texto longo",     icon: "fa fa-align-left",    category: "entrada" },
  { type: "number",    label: "Número",          icon: "fa fa-hashtag",       category: "entrada" },
  { type: "email",     label: "E-mail",          icon: "fa fa-envelope",      category: "entrada" },
  { type: "phone",     label: "Telefone",        icon: "fa fa-phone",         category: "entrada" },
  { type: "date",      label: "Data",            icon: "fa fa-calendar",      category: "entrada" },
  { type: "radio",     label: "Escolha única",   icon: "fa fa-dot-circle-o",  category: "escolha", hasOptions: true },
  { type: "checkbox",  label: "Múltipla escolha", icon: "fa fa-check-square-o", category: "escolha", hasOptions: true, multi: true },
  { type: "select",    label: "Lista suspensa",  icon: "fa fa-caret-square-o-down", category: "escolha", hasOptions: true },
  { type: "scale",     label: "Escala (0-10)",   icon: "fa fa-sliders",       category: "escolha" },
  { type: "yesno",     label: "Sim / Não",       icon: "fa fa-toggle-on",     category: "escolha", hasOptions: true },
  { type: "section",   label: "Seção / Título",  icon: "fa fa-header",        category: "layout" },
  { type: "paragraph", label: "Texto explicativo", icon: "fa fa-paragraph",   category: "layout" },
  { type: "cover",     label: "Capa / Boas-vindas", icon: "fa fa-flag",       category: "layout" },
  { type: "consent",   label: "Termos e Condições", icon: "fa fa-check-square-o", category: "layout" },
];

export const FIELD_TYPE_MAP = Object.fromEntries(FIELD_TYPES.map((f) => [f.type, f]));

// Derivado dos nomes já existentes, não de um contador de módulo: o contador zerava a cada
// reload e dois campos ficavam com o mesmo `name` — buildContext resolveria só o último.
function defaultName(type, existingFields) {
  const taken = new Set((existingFields || []).map((f) => f && f.name).filter(Boolean));
  let n = 1;
  while (taken.has(`${type}_${n}`)) n += 1;
  return `${type}_${n}`;
}

export function newField(type, existingFields = []) {
  const meta = FIELD_TYPE_MAP[type] || {};
  const id = genId("fld");
  const base = {
    id,
    type,
    name: defaultName(type, existingFields),
    label: meta.label || "Campo",
    required: false,
    placeholder: "",
    helpText: "",
    visibleIf: null,
    validation: {},
  };
  if (meta.hasOptions) {
    if (type === "yesno") {
      base.options = [
        { value: "sim", label: "Sim" },
        { value: "nao", label: "Não" },
      ];
    } else {
      base.options = [
        { value: "opcao_1", label: "Opção 1" },
        { value: "opcao_2", label: "Opção 2" },
      ];
    }
  }
  if (type === "section") base.label = "Nova seção";
  if (type === "paragraph") base.label = "Texto de apoio para o respondente.";
  if (type === "cover") {
    base.label = "Bem-vindo";
    base.body = "Escreva aqui a mensagem de boas-vindas ao respondente.";
    base.buttonLabel = "Começar";
  }
  if (type === "consent") {
    base.label = "Termos e Condições";
    base.body = "Cole aqui o texto completo dos termos e condições.";
    base.checkboxLabel = "Concordo com os termos e condições acima";
    base.required = true;
  }
  return base;
}

/* ------------------------------------------------- Referências a um campo/var */
// Identificadores inteiros da expressão: `peso` não pode casar com `peso_total`.
function exprRefs(expr) {
  return String(expr === undefined || expr === null ? "" : expr).match(/[A-Za-z_][A-Za-z0-9_]*/g) || [];
}

function conditionRefs(cond) {
  if (!cond || typeof cond !== "object") return [];
  if (Array.isArray(cond.rules)) return cond.rules.reduce((acc, r) => acc.concat(conditionRefs(r)), []);
  return cond.field === undefined || cond.field === null ? [] : [cond.field];
}

/**
 * Onde a definição referencia `refs` (id ou nome de campo, nome de derivação).
 * Comparação estrutural — nada de substring sobre o JSON. Retorna rótulos legíveis.
 */
export function findReferences(definition, refs) {
  const wanted = new Set((refs || []).filter(Boolean).map(String));
  const hits = (list) => (list || []).some((v) => v !== undefined && v !== null && wanted.has(String(v)));
  const def = definition || {};
  const out = [];

  (def.logic || []).forEach((rule, i) => {
    // skipTo aponta para página, não para campo
    const targets = (rule.actions || []).filter((a) => a.type !== "skipTo").map((a) => a.target);
    if (hits(conditionRefs(rule.when)) || hits(targets)) out.push(`Lógica: regra ${i + 1}`);
  });
  (def.calculations || []).forEach((c) => {
    if (hits(exprRefs(c.expression)) || hits([c.target])) out.push(`Cálculo: ${c.name || c.id}`);
  });
  (def.fields || []).forEach((f) => {
    if (hits(conditionRefs(f.visibleIf))) out.push(`Visibilidade: ${f.label || f.name || f.id}`);
  });

  const scoring = def.scoring || {};
  (scoring.derivations || []).forEach((d) => {
    const rowRefs = (d.rows || []).reduce((acc, r) => acc.concat(conditionRefs(r.when)), []);
    if (hits([d.input, d.source]) || hits(d.sources) || hits(exprRefs(d.expr)) || hits(rowRefs)) {
      out.push(`Escore: ${d.name || d.id}`);
    }
  });
  const output = scoring.output || {};
  if (hits(output.headline) || hits(output.lists) || hits((output.items || []).map((it) => it.var))) {
    out.push("Escore: saída");
  }
  return out;
}

// Grupo que ficaria sem regras vira null: `{op, rules: []}` é sempre verdadeiro.
function stripCondition(cond, wanted) {
  if (!cond || typeof cond !== "object") return cond || null;
  if (Array.isArray(cond.rules)) {
    const rules = cond.rules.map((r) => stripCondition(r, wanted)).filter(Boolean);
    return rules.length ? { ...cond, rules } : null;
  }
  return wanted.has(String(cond.field)) ? null : cond;
}

/**
 * Remove as regras órfãs que citam `refs`. Regra cuja condição sumiria por inteiro é
 * descartada: sem condição ela passaria a valer sempre (`is_empty(undefined)` é true).
 * Expressões de cálculo/derivação não são reescritas — resolvem para o `else`/0, e
 * reescrever texto de fórmula é o que traria de volta o casamento por substring.
 */
export function removeReferences(definition, refs) {
  const wanted = new Set((refs || []).filter(Boolean).map(String));
  const def = definition || {};
  const next = { ...def };

  if (def.logic) {
    next.logic = def.logic
      .map((rule) => ({
        ...rule,
        when: stripCondition(rule.when, wanted),
        actions: (rule.actions || []).filter((a) => a.type === "skipTo" || !wanted.has(String(a.target))),
      }))
      .filter((rule, i) => {
        if (def.logic[i].when && !rule.when) return false;
        return rule.actions.length > 0;
      });
  }
  if (def.fields) {
    next.fields = def.fields.map((f) => (f.visibleIf ? { ...f, visibleIf: stripCondition(f.visibleIf, wanted) } : f));
  }
  if (def.scoring && def.scoring.derivations) {
    next.scoring = {
      ...def.scoring,
      derivations: def.scoring.derivations.map((d) => {
        const patched = { ...d };
        if (d.sources) patched.sources = d.sources.filter((s) => !wanted.has(String(s)));
        if (d.rows) {
          patched.rows = d.rows
            .map((r) => ({ ...r, when: stripCondition(r.when, wanted) }))
            .filter((r, i) => !(d.rows[i].when && !r.when));
        }
        return patched;
      }),
    };
  }
  return next;
}

/**
 * Modelo de escore APA pronto (a antiga lógica cravada, agora como DADOS).
 * As `sources`/`input` referenciam NOMES de campos — ajuste no editor para
 * apontar aos campos do seu formulário (idade, peso, altura, cardiac, ...).
 */
export function apaSeedScoring() {
  return {
    enabled: true,
    derivations: [
      { id: genId("d"), name: "imc", type: "expression", expr: "peso / (altura*altura)" },
      { id: genId("d"), name: "imc_risco", type: "range", input: "imc",
        bands: [{ min: 30, max: "", output: 3 }], else: 1 },
      { id: genId("d"), name: "imc_classe", type: "range", input: "imc", bands: [
        { min: "", max: 18.5, output: "Baixo peso" },
        { min: 18.5, max: 25, output: "Peso normal" },
        { min: 25, max: 30, output: "Sobrepeso" },
        { min: 30, max: 35, output: "Obesidade I" },
        { min: 35, max: 40, output: "Obesidade II" },
        { min: 40, max: "", output: "Obesidade III" },
      ], else: "—" },
      { id: genId("d"), name: "idade_risco", type: "range", input: "idade",
        bands: [{ min: 70, max: "", output: 3 }], else: 1 },
      { id: genId("d"), name: "cardio_max", type: "aggregate", op: "max", sources: ["cardiac"], attribute: "score" },
      { id: genId("d"), name: "pulmonar_max", type: "aggregate", op: "max", sources: ["pulmonary"], attribute: "score" },
      { id: genId("d"), name: "endocrino_max", type: "aggregate", op: "max", sources: ["endocrine"], attribute: "score" },
      { id: genId("d"), name: "outras_max", type: "aggregate", op: "max", sources: ["others"], attribute: "score" },
      { id: genId("d"), name: "risco_num", type: "expression",
        expr: "max(idade_risco, imc_risco, cardio_max, pulmonar_max, endocrino_max, outras_max)" },
      { id: genId("d"), name: "risco_paciente", type: "map", input: "risco_num", pairs: [
        { when: "1", then: "Muito Baixo" }, { when: "2", then: "Baixo" },
        { when: "3", then: "Intermediário" }, { when: "4", then: "Alto" },
        { when: "5", then: "Muito Alto" },
      ], else: "Muito Baixo" },
      { id: genId("d"), name: "asa", type: "decision", strategy: "max", rows: [
        { when: { op: "and", rules: [{ field: "risco_num", operator: "gte", value: "4" }] }, output: 3 },
        { when: { op: "and", rules: [{ field: "risco_num", operator: "gte", value: "2" }] }, output: 2 },
      ], else: 1 },
      { id: genId("d"), name: "medicamentos", type: "collect", source: "medications", attribute: "suspensionDays" },
    ],
    output: {
      headline: ["risco_paciente", "asa"],
      items: [
        { label: "IMC", var: "imc" },
        { label: "Classificação IMC", var: "imc_classe" },
      ],
      lists: ["medicamentos"],
    },
  };
}

export function emptyDefinition() {
  return {
    pages: [{ id: "page_1", title: "Página 1", fieldIds: [] }],
    fields: [],
    logic: [],
    calculations: [],
    theme: {},
  };
}

/* ------------------------------------------------------------- API client -- */
export const FormsApi = {
  list: (queryString = "") => http.get(`${API_RESOURCE}${queryString}`),
  get: (id) => http.get(`${API_RESOURCE}/${id}`),
  create: (data) => http.post(`${API_RESOURCE}`, data),
  update: (id, data) => http.put(`${API_RESOURCE}/${id}`, data),
  remove: (id) => http.delete(`${API_RESOURCE}/${id}`),
  publish: (id) => http.post(`${API_RESOURCE}/${id}/publish`),
  saveDraft: (id, definition) => http.put(`${API_RESOURCE}/${id}/draft`, { definition }),
  importJotform: (data) => http.post(`${API_RESOURCE}/import/jotform`, data),

  listInvitations: (id, queryString = "") => http.get(`${API_RESOURCE}/${id}/invitations${queryString}`),
  createInvitations: (id, payload) => http.post(`${API_RESOURCE}/${id}/invitations`, payload),
  sendInvitation: (id, invitationId, payload = {}) => http.post(`${API_RESOURCE}/${id}/invitations/${invitationId}/send`, payload),
  deleteInvitation: (id, invitationId) => http.delete(`${API_RESOURCE}/${id}/invitations/${invitationId}`),

  listResponses: (id, queryString = "") => http.get(`${API_RESOURCE}/${id}/responses${queryString}`),
  getResponse: (id, responseId) => http.get(`${API_RESOURCE}/${id}/responses/${responseId}`),
  getInsumo: (id, responseId) => http.get(`${API_RESOURCE}/${id}/responses/${responseId}/insumo`),

  // Dashboard
  getDashboard: (queryString = "") => http.get(`${API_RESOURCE}/dashboard/funnel${queryString}`),
  getDashboardList: (queryString = "") => http.get(`${API_RESOURCE}/dashboard/funnel/list${queryString}`),
  getInteressados: (queryString = "") => http.get(`${API_RESOURCE}/dashboard/interessados${queryString}`),

  // WhatsApp: configuração + templates
  getWhatsappSettings: () => http.get(`${API_RESOURCE}/whatsapp/settings`),
  saveWhatsappSettings: (data) => http.put(`${API_RESOURCE}/whatsapp/settings`, data),
  listTemplates: () => http.get(`${API_RESOURCE}/whatsapp/templates`),
  listTwilioTemplates: () => http.get(`${API_RESOURCE}/whatsapp/twilio-templates`),
  createTemplate: (data) => http.post(`${API_RESOURCE}/whatsapp/templates`, data),
  updateTemplate: (templateId, data) => http.put(`${API_RESOURCE}/whatsapp/templates/${templateId}`, data),
  deleteTemplate: (templateId) => http.delete(`${API_RESOURCE}/whatsapp/templates/${templateId}`),
};

export const CAMPAIGN_RESOURCE = "form-campaigns";

export const CampaignsApi = {
  list: (queryString = "") => http.get(`${CAMPAIGN_RESOURCE}${queryString}`),
  show: (id) => http.get(`${CAMPAIGN_RESOURCE}/${id}`),
  create: (formData) =>
    http.post(`${CAMPAIGN_RESOURCE}`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  dispatch: (id) => http.post(`${CAMPAIGN_RESOURCE}/${id}/dispatch`),
};

/** Gera e baixa o modelo CSV (nome, telefone, cpf) no próprio cliente. */
export function downloadCampaignTemplate() {
  const csv = "nome,telefone,cpf\nJoão da Silva,5511999998888,12345678900\nMaria Souza,5511988887777,98765432100";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "modelo-campanha.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// Chaves clínicas (APA) — marcam o papel de cada campo no escore pré-anestésico
export const CLINICAL_KEYS = [
  { value: "", label: "— nenhuma —" },
  { value: "name", label: "Nome completo" },
  { value: "age", label: "Idade" },
  { value: "weight", label: "Peso (kg)" },
  { value: "height", label: "Altura (m)" },
  { value: "cpf", label: "CPF" },
  { value: "phone", label: "Telefone" },
  { value: "sex", label: "Sexo" },
  { value: "pregnancy", label: "Gravidez" },
  { value: "cardiac", label: "Condições cardíacas (pesos)" },
  { value: "pulmonary", label: "Condições pulmonares (pesos)" },
  { value: "endocrine", label: "Condições endócrinas (pesos)" },
  { value: "diabetes", label: "Diabetes (sim/não)" },
  { value: "diabetes_insulin", label: "Diabetes insulino-dependente" },
  { value: "others", label: "Outras condições (pesos)" },
  { value: "smoking", label: "Tabagismo" },
  { value: "alcohol", label: "Etilismo" },
  { value: "drugs", label: "Outras drogas" },
  { value: "medications", label: "Medicamentos (suspensão)" },
  { value: "anesthesia_feeling", label: "Sentimento sobre anestesia" },
  { value: "consult_preference", label: "Preferência de consulta" },
];

// Quais clinicalKeys usam peso de risco por opção
export const CLINICAL_KEYS_WITH_SCORE = ["cardiac", "pulmonary", "endocrine", "others"];

// Nacionalidades do campo CPF quando marcado "Estrangeiro". O `value` é a sigla ISO de duas
// letras porque é ela que vai para o `nationality` do D2I, e o robô só vincula a resposta ao
// agendamento quando RNE e nacionalidade batem com o que está lá. Nome por extenso não casa.
// A lista é a ISO 3166-1 em vigor, sem opção "Outra": qualquer texto que não seja sigla
// deixaria a resposta órfã, e sem a lista completa o paciente de país ausente não envia.
// País extinto e código retirado ficam de fora: duplicavam o nome na tela, e escolher
// o item errado grava sigla que o D2I não tem.
export const NATIONALITIES = [
  ["AF", "Afeganistão"], ["ZA", "África do Sul"],
  ["AL", "Albânia"], ["DE", "Alemanha"],
  ["AD", "Andorra"], ["AO", "Angola"],
  ["AI", "Anguila"], ["AQ", "Antártida"],
  ["AG", "Antígua e Barbuda"], ["SA", "Arábia Saudita"],
  ["DZ", "Argélia"], ["AR", "Argentina"],
  ["AM", "Armênia"], ["AW", "Aruba"],
  ["AU", "Austrália"], ["AT", "Áustria"],
  ["AZ", "Azerbaijão"], ["BS", "Bahamas"],
  ["BD", "Bangladesh"], ["BB", "Barbados"],
  ["BH", "Barein"], ["BE", "Bélgica"],
  ["BZ", "Belize"], ["BJ", "Benin"],
  ["BM", "Bermudas"], ["BY", "Bielorrússia"],
  ["BO", "Bolívia"], ["BA", "Bósnia e Herzegovina"],
  ["BW", "Botsuana"], ["BR", "Brasil"],
  ["BN", "Brunei"], ["BG", "Bulgária"],
  ["BF", "Burquina Faso"], ["BI", "Burundi"],
  ["BT", "Butão"], ["CV", "Cabo Verde"],
  ["CM", "Camarões"], ["KH", "Camboja"],
  ["CA", "Canadá"], ["QA", "Catar"],
  ["KZ", "Cazaquistão"], ["TD", "Chade"],
  ["CL", "Chile"], ["CN", "China"],
  ["CY", "Chipre"], ["VA", "Cidade do Vaticano"],
  ["CO", "Colômbia"], ["KM", "Comores"],
  ["CD", "Congo - Kinshasa"], ["KP", "Coreia do Norte"],
  ["KR", "Coreia do Sul"], ["CI", "Costa do Marfim"],
  ["CR", "Costa Rica"], ["HR", "Croácia"],
  ["CU", "Cuba"], ["CW", "Curaçao"],
  ["DK", "Dinamarca"], ["DJ", "Djibuti"],
  ["DM", "Dominica"], ["EG", "Egito"],
  ["SV", "El Salvador"], ["AE", "Emirados Árabes Unidos"],
  ["EC", "Equador"], ["ER", "Eritreia"],
  ["SK", "Eslováquia"], ["SI", "Eslovênia"],
  ["ES", "Espanha"], ["SZ", "Essuatíni"],
  ["US", "Estados Unidos"], ["EE", "Estônia"],
  ["ET", "Etiópia"], ["FJ", "Fiji"],
  ["PH", "Filipinas"], ["FI", "Finlândia"],
  ["FR", "França"], ["GA", "Gabão"],
  ["GM", "Gâmbia"], ["GH", "Gana"],
  ["GE", "Geórgia"], ["GI", "Gibraltar"],
  ["GD", "Granada"], ["GR", "Grécia"],
  ["GL", "Groenlândia"], ["GP", "Guadalupe"],
  ["GU", "Guam"], ["GT", "Guatemala"],
  ["GG", "Guernsey"], ["GY", "Guiana"],
  ["GF", "Guiana Francesa"], ["GN", "Guiné"],
  ["GQ", "Guiné Equatorial"], ["GW", "Guiné-Bissau"],
  ["HT", "Haiti"], ["HN", "Honduras"],
  ["HK", "Hong Kong, RAE da China"], ["HU", "Hungria"],
  ["YE", "Iêmen"], ["BV", "Ilha Bouvet"],
  ["CX", "Ilha Christmas"], ["IM", "Ilha de Man"],
  ["NF", "Ilha Norfolk"], ["AX", "Ilhas Aland"],
  ["KY", "Ilhas Cayman"], ["CC", "Ilhas Cocos (Keeling)"],
  ["CK", "Ilhas Cook"], ["FO", "Ilhas Faroé"],
  ["GS", "Ilhas Geórgia do Sul e Sandwich do Sul"], ["HM", "Ilhas Heard e McDonald"],
  ["FK", "Ilhas Malvinas"], ["MP", "Ilhas Marianas do Norte"],
  ["MH", "Ilhas Marshall"], ["UM", "Ilhas Menores Distantes dos EUA"],
  ["PN", "Ilhas Pitcairn"], ["SB", "Ilhas Salomão"],
  ["TC", "Ilhas Turcas e Caicos"], ["VI", "Ilhas Virgens Americanas"],
  ["VG", "Ilhas Virgens Britânicas"], ["IN", "Índia"],
  ["ID", "Indonésia"], ["IR", "Irã"],
  ["IQ", "Iraque"], ["IE", "Irlanda"],
  ["IS", "Islândia"], ["IL", "Israel"],
  ["IT", "Itália"], ["JM", "Jamaica"],
  ["JP", "Japão"], ["JE", "Jersey"],
  ["JO", "Jordânia"], ["XK", "Kosovo"],
  ["KW", "Kuwait"], ["LA", "Laos"],
  ["LS", "Lesoto"], ["LV", "Letônia"],
  ["LB", "Líbano"], ["LR", "Libéria"],
  ["LY", "Líbia"], ["LI", "Liechtenstein"],
  ["LT", "Lituânia"], ["LU", "Luxemburgo"],
  ["MO", "Macau, RAE da China"], ["MK", "Macedônia do Norte"],
  ["MG", "Madagascar"], ["MY", "Malásia"],
  ["MW", "Malaui"], ["MV", "Maldivas"],
  ["ML", "Mali"], ["MT", "Malta"],
  ["MA", "Marrocos"], ["MQ", "Martinica"],
  ["MU", "Maurício"], ["MR", "Mauritânia"],
  ["YT", "Mayotte"], ["MX", "México"],
  ["MM", "Mianmar (Birmânia)"], ["FM", "Micronésia"],
  ["MZ", "Moçambique"], ["MD", "Moldávia"],
  ["MC", "Mônaco"], ["MN", "Mongólia"],
  ["ME", "Montenegro"], ["MS", "Montserrat"],
  ["NA", "Namíbia"], ["NR", "Nauru"],
  ["NP", "Nepal"], ["NI", "Nicarágua"],
  ["NE", "Níger"], ["NG", "Nigéria"],
  ["NU", "Niue"], ["NO", "Noruega"],
  ["NC", "Nova Caledônia"], ["NZ", "Nova Zelândia"],
  ["OM", "Omã"], ["NL", "Países Baixos"],
  ["BQ", "Países Baixos Caribenhos"], ["PW", "Palau"],
  ["PA", "Panamá"], ["PG", "Papua-Nova Guiné"],
  ["PK", "Paquistão"], ["PY", "Paraguai"],
  ["PE", "Peru"], ["PF", "Polinésia Francesa"],
  ["PL", "Polônia"], ["PR", "Porto Rico"],
  ["PT", "Portugal"], ["KE", "Quênia"],
  ["KG", "Quirguistão"], ["KI", "Quiribati"],
  ["GB", "Reino Unido"], ["CF", "República Centro-Africana"],
  ["CG", "República do Congo"], ["DO", "República Dominicana"],
  ["RE", "Reunião"], ["RO", "Romênia"],
  ["RW", "Ruanda"], ["RU", "Rússia"],
  ["EH", "Saara Ocidental"], ["WS", "Samoa"],
  ["AS", "Samoa Americana"], ["SM", "San Marino"],
  ["SH", "Santa Helena"], ["LC", "Santa Lúcia"],
  ["BL", "São Bartolomeu"], ["KN", "São Cristóvão e Névis"],
  ["MF", "São Martinho"], ["PM", "São Pedro e Miquelão"],
  ["ST", "São Tomé e Príncipe"], ["VC", "São Vicente e Granadinas"],
  ["SC", "Seicheles"], ["SN", "Senegal"],
  ["SL", "Serra Leoa"], ["RS", "Sérvia"],
  ["SG", "Singapura"], ["SX", "Sint Maarten"],
  ["SY", "Síria"], ["SO", "Somália"],
  ["LK", "Sri Lanka"], ["SD", "Sudão"],
  ["SS", "Sudão do Sul"], ["SE", "Suécia"],
  ["CH", "Suíça"], ["SR", "Suriname"],
  ["SJ", "Svalbard e Jan Mayen"], ["TJ", "Tadjiquistão"],
  ["TH", "Tailândia"], ["TW", "Taiwan"],
  ["TZ", "Tanzânia"], ["CZ", "Tchéquia"],
  ["IO", "Território Britânico do Oceano Índico"], ["TF", "Territórios Franceses do Sul"],
  ["PS", "Territórios palestinos"], ["TL", "Timor-Leste"],
  ["TG", "Togo"], ["TK", "Tokelau"],
  ["TO", "Tonga"], ["TT", "Trinidad e Tobago"],
  ["TN", "Tunísia"], ["TM", "Turcomenistão"],
  ["TR", "Turquia"], ["TV", "Tuvalu"],
  ["UA", "Ucrânia"], ["UG", "Uganda"],
  ["UY", "Uruguai"], ["UZ", "Uzbequistão"],
  ["VU", "Vanuatu"], ["VE", "Venezuela"],
  ["VN", "Vietnã"], ["WF", "Wallis e Futuna"],
  ["ZM", "Zâmbia"], ["ZW", "Zimbábue"],
].map(([value, label]) => ({ value, label }));

// Resposta começada antes da troca guarda o nome do país em vez da sigla. Converter só na
// tela deixaria o valor velho seguir para o D2I, que não reconhece nome por extenso.
export function normalizeNationalities(values) {
  const saida = {};
  Object.entries(values || {}).forEach(([id, valor]) => {
    const estrangeiro = valor && typeof valor === "object" && valor.foreign;
    saida[id] = estrangeiro ? { ...valor, nationality: toNationalityCode(valor.nationality) } : valor;
  });
  return saida;
}

export function toNationalityCode(valor) {
  const texto = String(valor == null ? "" : valor).trim();
  if (!texto) return "";
  if (NATIONALITIES.some((n) => n.value === texto)) return texto;
  const semAcento = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
  const achado = NATIONALITIES.find((n) => semAcento(n.label) === semAcento(texto));
  return achado ? achado.value : "";
}

// Fontes de variáveis disponíveis para mapear num template de WhatsApp
export const WHATSAPP_VAR_SOURCES = [
  { value: "first_name", label: "Primeiro nome do contato" },
  { value: "contact_name", label: "Nome completo do contato" },
  { value: "form_link", label: "Link do formulário" },
  { value: "form_title", label: "Título do formulário" },
  { value: "contact_email", label: "E-mail do contato" },
  { value: "custom", label: "Texto fixo" },
];
